import React, { useState, useMemo } from "react";
import { Button, Card, Col, Input, Row, Select, Space, Statistic, Tooltip } from "antd";
import { CustomTable } from "../series/StyledComponent";
import { ScheduleDataItem } from "./types";
import { getStartingUnits, calculateUtilization } from "./utils/slotTableUtils";

interface InventorySummaryComponentProps {
    scheduleData: { [key: string]: ScheduleDataItem };
    loading: boolean;
    getAdSelectionCounts: () => { [scheduleName: string]: number };
    getOriginalAdSelectionCounts: () => { [scheduleName: string]: number };
}

export const InventorySummaryComponent: React.FC<InventorySummaryComponentProps> = ({
    scheduleData,
    loading,
    getAdSelectionCounts,
    getOriginalAdSelectionCounts,
}) => {
    const [inventorySearchText, setInventorySearchText] = useState<string>('');
    const [inventoryAdvertiserFilter, setInventoryAdvertiserFilter] = useState<string | undefined>(undefined);
    const [inventoryStatusFilter, setInventoryStatusFilter] = useState<string | undefined>(undefined);
    const [inventoryLengthFilter, setInventoryLengthFilter] = useState<number | undefined>(undefined);
    const [inventoryDayOfWeekFilter, setInventoryDayOfWeekFilter] = useState<string[]>([]);

    // Get color based on utilization percentage (100% = green, < 100% = red)
    const getUtilizationColor = (utilization: number): string => {
        return utilization === 100 ? '#52c41a' : '#ff4d4f'; // Green for 100%, Red for < 100%
    };

    const calculateInventoryStats = () => {
        const schedules = Object.values(scheduleData);
        const totalAds = schedules.length;
        let totalUnitsRemaining = 0;
        let totalStartingUnits = 0;
        const adSelectionCounts = getAdSelectionCounts();

        schedules.forEach((schedule) => {
            totalUnitsRemaining += schedule.UNITS_REMAINING;
            totalStartingUnits += getStartingUnits(schedule) - (schedule.PROGRESSIVE_PODDING_ADJUSTMENT ?? 0);
        });

        // Calculate projected remaining based on starting inventory minus total selections
        const totalSelections = Object.values(adSelectionCounts).reduce((sum, count) => sum + count, 0);
        const projectedUnitsRemaining = Math.max(0, totalStartingUnits - totalSelections);

        const overallUtilization = totalStartingUnits > 0
            ? Math.round(((totalStartingUnits - totalUnitsRemaining) / totalStartingUnits) * 100)
            : 0;

        const projectedUtilization = totalStartingUnits > 0
            ? Math.round(((totalStartingUnits - projectedUnitsRemaining) / totalStartingUnits) * 100)
            : 0;

        return {
            totalAds,
            totalUnitsRemaining,
            projectedUnitsRemaining,
            totalStartingUnits,
            overallUtilization,
            projectedUtilization,
            totalSelections: Object.values(adSelectionCounts).reduce((sum, count) => sum + count, 0)
        };
    };

    // Get filtered inventory data
    const getFilteredInventoryData = (): ScheduleDataItem[] => {
        let filtered = Object.values(scheduleData);

        // Filter by search text (schedule name, advertiser, brand)
        if (inventorySearchText) {
            const searchLower = inventorySearchText.toLowerCase();
            filtered = filtered.filter(item => 
                item.SCHEDULE_NAME?.toLowerCase().includes(searchLower) ||
                item.ADVERTISER_NAME?.toLowerCase().includes(searchLower) ||
                item.DEALBRAND?.toLowerCase().includes(searchLower)
            );
        }

        // Filter by advertiser
        if (inventoryAdvertiserFilter) {
            filtered = filtered.filter(item => item.ADVERTISER_ID === inventoryAdvertiserFilter);
        }

        // Filter by status (utilization)
        if (inventoryStatusFilter) {
            filtered = filtered.filter(item => {
                const utilization = calculateUtilization(getStartingUnits(item) - (item.PROGRESSIVE_PODDING_ADJUSTMENT ?? 0), item.UNITS_REMAINING);
                if (inventoryStatusFilter === '100') {
                    return utilization === 100;
                } else if (inventoryStatusFilter === 'less-than-100') {
                    return utilization < 100;
                }
                return true;
            });
        }

        // Filter by length
        if (inventoryLengthFilter !== undefined) {
            filtered = filtered.filter(item => item.UNITLENGTH === inventoryLengthFilter);
        }

        // Filter by day of week (multiple selection)
        if (inventoryDayOfWeekFilter && inventoryDayOfWeekFilter.length > 0) {
            filtered = filtered.filter(item => {
                const daysAllowedStr = item.DAYS_ALLOWED || '';
                const allowedDays = daysAllowedStr.split(',').map(d => d.trim());
                // Check if ANY of the selected days is in the allowed days (case-insensitive)
                return inventoryDayOfWeekFilter.some(selectedDay => {
                    return allowedDays.some(day => 
                        day.toLowerCase() === selectedDay.toLowerCase() ||
                        day.substring(0, 3).toLowerCase() === selectedDay.substring(0, 3).toLowerCase()
                    );
                });
            });
        }

        return filtered;
    };

    // Get unique advertisers for filter dropdown, sorted alphabetically
    const getUniqueAdvertisers = (): { label: string; value: string; count: number }[] => {
        const advertisers = new Map<string, { name: string; count: number }>();
        Object.values(scheduleData).forEach(item => {
            if (item.ADVERTISER_ID && item.ADVERTISER_NAME) {
                const existing = advertisers.get(item.ADVERTISER_ID);
                if (existing) {
                    existing.count++;
                } else {
                    advertisers.set(item.ADVERTISER_ID, { name: item.ADVERTISER_NAME, count: 1 });
                }
            }
        });
        return Array.from(advertisers.entries())
            .map(([id, data]) => ({
                label: data.name,
                value: id,
                count: data.count
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    };

    // Get unique lengths for filter dropdown, sorted numerically
    const getUniqueLengths = (): { label: string; value: number; count: number }[] => {
        const lengths = new Map<number, number>();
        Object.values(scheduleData).forEach(item => {
            if (item.UNITLENGTH !== undefined && item.UNITLENGTH !== null) {
                lengths.set(item.UNITLENGTH, (lengths.get(item.UNITLENGTH) || 0) + 1);
            }
        });
        return Array.from(lengths.entries())
            .map(([length, count]) => ({
                label: `${length}s`,
                value: length,
                count: count
            }))
            .sort((a, b) => a.value - b.value);
    };

    // Get unique days of week for filter dropdown
    const getUniqueDaysOfWeek = (): { label: string; value: string; count: number }[] => {
        const dayCounts = new Map<string, number>();
        const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        
        Object.values(scheduleData).forEach(item => {
            const daysAllowedStr = item.DAYS_ALLOWED || '';
            const allowedDays = daysAllowedStr.split(',').map(d => d.trim()).filter(d => d);
            allowedDays.forEach(day => {
                // Normalize day names (handle variations like "Monday" vs "Mon")
                const normalizedDay = dayOrder.find(d => 
                    d.toLowerCase() === day.toLowerCase() || 
                    d.toLowerCase() === day.substring(0, 3).toLowerCase()
                ) || day;
                dayCounts.set(normalizedDay, (dayCounts.get(normalizedDay) || 0) + 1);
            });
        });
        
        return dayOrder
            .filter(day => dayCounts.has(day))
            .map(day => ({
                label: day,
                value: day,
                count: dayCounts.get(day) || 0
            }));
    };

    const inventoryColumns = [
        {
            title: 'Schedule Name',
            dataIndex: 'SCHEDULE_NAME',
            key: 'SCHEDULE_NAME',
        },
        {
            title: 'Advertiser',
            dataIndex: 'ADVERTISER_NAME',
            key: 'ADVERTISER_NAME',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: {
                setSelectedKeys: (keys: React.Key[]) => void;
                selectedKeys: React.Key[];
                confirm: () => void;
                clearFilters?: () => void;
            }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search advertiser"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                if (clearFilters) clearFilters();
                                confirm();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value: string | number | boolean, record: ScheduleDataItem) =>
                record.ADVERTISER_NAME?.toLowerCase().includes(String(value).toLowerCase()) || false,
        },
        {
            title: 'Brand',
            dataIndex: 'DEALBRAND',
            key: 'DEALBRAND',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: {
                setSelectedKeys: (keys: React.Key[]) => void;
                selectedKeys: React.Key[];
                confirm: () => void;
                clearFilters?: () => void;
            }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search brand"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                if (clearFilters) clearFilters();
                                confirm();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value: string | number | boolean, record: ScheduleDataItem) =>
                record.DEALBRAND?.toLowerCase().includes(String(value).toLowerCase()) || false,
        },
        {
            title: 'Commercial Type',
            dataIndex: 'COMMERCIAL_TYPE',
            key: 'COMMERCIAL_TYPE',
            render: (commercialType: string | undefined) => commercialType || '-',
        },
        {
            title: 'Length',
            dataIndex: 'UNITLENGTH',
            key: 'UNITLENGTH',
            render: (length: number) => `${length}s`,
        },
        {
            title: 'Units Remaining',
            dataIndex: 'UNITS_REMAINING',
            key: 'UNITS_REMAINING',
            render: (remaining: number, record: ScheduleDataItem) => {
                // Calculate projected remaining accounting for original vs current selections
                const currentSelectionCounts = getAdSelectionCounts();
                const originalSelectionCounts = getOriginalAdSelectionCounts();
                const currentCount = currentSelectionCounts[record.SCHEDULE_NAME] || 0;
                const originalCount = originalSelectionCounts[record.SCHEDULE_NAME] || 0;
                // Start from DB remaining, add back original selections, subtract current selections
                const projectedRemaining = Math.max(0, record.UNITS_REMAINING + originalCount - currentCount);
                const isFullyUtilized = projectedRemaining === 0 && currentCount > 0;
                const startingUnits = getStartingUnits(record);
                const progressiveAdjustment = record.PROGRESSIVE_PODDING_ADJUSTMENT ?? 0;
                const effectiveStartingUnits = startingUnits - progressiveAdjustment;
                const displayRemaining = currentCount > 0 ? projectedRemaining : remaining;
                const utilization = calculateUtilization(effectiveStartingUnits, displayRemaining);
                const color = getUtilizationColor(utilization);

                return (
                    <div>
                        <span style={{ color: color, fontWeight: 500 }}>
                            {displayRemaining} / {effectiveStartingUnits}
                        </span>
                        {progressiveAdjustment > 0 && (
                            <div style={{
                                fontSize: '11px',
                                color: '#999999',
                                marginTop: 2
                            }}>
                                {startingUnits} → {effectiveStartingUnits} units (progressive podding)
                            </div>
                        )}
                        {currentCount > 0 && (
                            <div style={{
                                fontSize: '11px',
                                color: isFullyUtilized ? '#52c41a' : '#faad14',
                                marginTop: 2
                            }}>
                                {currentCount} unit{currentCount > 1 ? 's' : ''} assigned{isFullyUtilized ? ' (100% fill)' : ''}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Day of Week Usage',
            key: 'DAY_OF_WEEK_USAGE',
            render: (_: unknown, record: ScheduleDataItem) => {
                const days = [
                    { key: 'DAY_OF_WEEK_USAGE_MON', label: 'Mon', code: 'Mon' },
                    { key: 'DAY_OF_WEEK_USAGE_TUE', label: 'Tue', code: 'Tue' },
                    { key: 'DAY_OF_WEEK_USAGE_WED', label: 'Wed', code: 'Wed' },
                    { key: 'DAY_OF_WEEK_USAGE_THU', label: 'Thu', code: 'Thu' },
                    { key: 'DAY_OF_WEEK_USAGE_FRI', label: 'Fri', code: 'Fri' },
                    { key: 'DAY_OF_WEEK_USAGE_SAT', label: 'Sat', code: 'Sat' },
                    { key: 'DAY_OF_WEEK_USAGE_SUN', label: 'Sun', code: 'Sun' },
                ];
                
                // Parse allowed days from comma-separated string
                const daysAllowedStr = record.DAYS_ALLOWED || '';
                const allowedDaysSet = new Set(
                    daysAllowedStr.split(',').map(d => d.trim())
                );
                
                const allowedParts: string[] = [];
                const notAllowedParts: string[] = [];
                
                days.forEach(day => {
                    const count = record[day.key as keyof ScheduleDataItem] as number | undefined;
                    const dayStr = `${day.label}:${count ?? 0}`;
                    
                    if (allowedDaysSet.has(day.code) || allowedDaysSet.has(day.label)) {
                        allowedParts.push(dayStr);
                    } else {
                        notAllowedParts.push(dayStr);
                    }
                });
                
                return (
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.6' }}>
                        {allowedParts.length > 0 && (
                            <div>
                                <span style={{ fontWeight: 500 }}>Allowed: </span>
                                <span>{allowedParts.join(' ')}</span>
                            </div>
                        )}
                        {notAllowedParts.length > 0 && (
                            <div style={{ marginTop: 4, color: '#999999' }}>
                                <span>Not allowed: </span>
                                <span>{notAllowedParts.join(' ')}</span>
                            </div>
                        )}
                        {allowedParts.length === 0 && notAllowedParts.length === 0 && (
                            <div>No data</div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Days of Air',
            dataIndex: 'DAYS_OF_AIR',
            key: 'DAYS_OF_AIR',
            render: (daysOfAir: string | undefined) => {
                if (!daysOfAir) return <span style={{ color: '#999' }}>-</span>;
                const tokens = daysOfAir.split(',').map(t => t.trim()).filter(Boolean);
                if (tokens.length === 0) return <span style={{ color: '#999' }}>-</span>;
                const capitalized = tokens.map(t => t.charAt(0).toUpperCase() + t.slice(1));
                return <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{capitalized.join(', ')}</span>;
            },
        },
        {
            title: 'Max Per Day',
            dataIndex: 'MAX_PER_DAY',
            key: 'MAX_PER_DAY',
            render: (maxPerDay: number | undefined) => {
                if (maxPerDay === undefined || maxPerDay === null || maxPerDay === 0) {
                    return <span style={{ color: '#999' }}>No limit</span>;
                }
                return <span style={{ fontWeight: 500 }}>{maxPerDay}</span>;
            },
        },
        {
            title: 'Min Separation',
            dataIndex: 'MIN_SEP',
            key: 'MIN_SEP',
            render: (minSep: number | undefined) => {
                if (minSep === undefined || minSep === null || minSep === 0) {
                    return <span style={{ color: '#999' }}>No separation</span>;
                }
                return <span style={{ fontWeight: 500 }}>{minSep} min</span>;
            },
        },
        {
            title: 'Advertiser Max Per Day',
            dataIndex: 'ADV_MAX_PER_DAY_CALC',
            key: 'ADV_MAX_PER_DAY_CALC',
            render: (advMaxPerDayCalc: number | undefined) => {
                if (advMaxPerDayCalc === undefined || advMaxPerDayCalc === null || advMaxPerDayCalc === 0) {
                    return <span style={{ color: '#999' }}>No limit</span>;
                }
                return <span style={{ fontWeight: 500 }}>{advMaxPerDayCalc}</span>;
            },
        },
        {
            title: 'Advertiser Separation',
            dataIndex: 'ADV_SEP',
            key: 'ADV_SEP',
            render: (advSep: number | undefined) => {
                if (advSep === undefined || advSep === null || advSep === 0) {
                    return <span style={{ color: '#999' }}>No separation</span>;
                }
                return <span style={{ fontWeight: 500 }}>{advSep} min</span>;
            },
        },
        {
            title: 'Utilization',
            key: 'UTILIZATION',
            render: (_: unknown, record: ScheduleDataItem) => {
                const utilization = calculateUtilization(getStartingUnits(record) - (record.PROGRESSIVE_PODDING_ADJUSTMENT ?? 0), record.UNITS_REMAINING);
                const color = getUtilizationColor(utilization);
                return (
                    <span style={{ color: color, fontWeight: 500 }}>
                        {utilization}%
                    </span>
                );
            },
        },
        {
            title: 'Rate',
            dataIndex: 'RATE_STR',
            key: 'RATE_STR',
            render: (rateStr: string) => rateStr,
        },
        {
            title: 'Normalised Rate',
            dataIndex: 'RATE_INT_NORMALISED',
            key: 'RATE_INT_NORMALISED',
            sorter: (a: ScheduleDataItem, b: ScheduleDataItem) => {
                const aVal = a.RATE_INT_NORMALISED ?? 0;
                const bVal = b.RATE_INT_NORMALISED ?? 0;
                return aVal - bVal;
            },
            defaultSortOrder: 'descend' as const,
            render: (normalisedRate: number | undefined) => {
                if (normalisedRate === undefined || normalisedRate === null) {
                    return '-';
                }
                return `$${normalisedRate.toLocaleString()}`;
            },
        },
    ];

    const stats = useMemo(() => calculateInventoryStats(), [scheduleData, getAdSelectionCounts]);
    const filteredData = useMemo(() => getFilteredInventoryData(), [
        scheduleData,
        inventorySearchText,
        inventoryAdvertiserFilter,
        inventoryStatusFilter,
        inventoryLengthFilter,
        inventoryDayOfWeekFilter,
    ]);
    const uniqueAdvertisers = useMemo(() => getUniqueAdvertisers(), [scheduleData]);
    const uniqueLengths = useMemo(() => getUniqueLengths(), [scheduleData]);
    const uniqueDaysOfWeek = useMemo(() => getUniqueDaysOfWeek(), [scheduleData]);

    const handleClearFilters = () => {
        setInventorySearchText('');
        setInventoryAdvertiserFilter(undefined);
        setInventoryStatusFilter(undefined);
        setInventoryLengthFilter(undefined);
        setInventoryDayOfWeekFilter([]);
    };

    return (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Card 
                title="Inventory Listing"
                extra={
                    <span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>
                        Showing {filteredData.length} of {Object.keys(scheduleData).length} ads
                    </span>
                }
            >
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={5}>
                        <Input
                            placeholder="Search by schedule, advertiser, or brand"
                            value={inventorySearchText}
                            onChange={(e) => setInventorySearchText(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col span={4}>
                        <Select
                            placeholder="Filter by Advertiser"
                            value={inventoryAdvertiserFilter}
                            onChange={setInventoryAdvertiserFilter}
                            allowClear
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp="label"
                        >
                            {uniqueAdvertisers.map(advertiser => (
                                <Select.Option 
                                    key={advertiser.value} 
                                    value={advertiser.value}
                                    label={advertiser.label}
                                >
                                    {advertiser.label} ({advertiser.count} {advertiser.count === 1 ? 'schedule' : 'schedules'})
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select
                            placeholder="Filter by Utilization"
                            value={inventoryStatusFilter}
                            onChange={setInventoryStatusFilter}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            <Select.Option value="100">
                                100% Utilization
                            </Select.Option>
                            <Select.Option value="less-than-100">
                                &lt; 100% Utilization
                            </Select.Option>
                        </Select>
                    </Col>
                    <Col span={3}>
                        <Select
                            placeholder="Filter by Length"
                            value={inventoryLengthFilter}
                            onChange={setInventoryLengthFilter}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            {uniqueLengths.map(length => (
                                <Select.Option 
                                    key={length.value} 
                                    value={length.value}
                                >
                                    {length.label} ({length.count} {length.count === 1 ? 'schedule' : 'schedules'})
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select
                            mode="multiple"
                            placeholder="Filter by Day of Week"
                            value={inventoryDayOfWeekFilter}
                            onChange={setInventoryDayOfWeekFilter}
                            allowClear
                            style={{ width: '100%' }}
                            maxTagCount="responsive"
                        >
                            {uniqueDaysOfWeek.map(day => (
                                <Select.Option 
                                    key={day.value} 
                                    value={day.value}
                                >
                                    {day.label} ({day.count} {day.count === 1 ? 'schedule' : 'schedules'})
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Button
                            onClick={handleClearFilters}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
                <CustomTable
                    columns={inventoryColumns as never}
                    dataSource={filteredData}
                    loading={loading}
                    pagination={{ pageSize: 20 }}
                    bordered={true}
                    rowKey="ID"
                />
            </Card>
        </Space>
    );
};
